import React, { useDeferredValue, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa";
import adminApi from "services/admin-api";
import { DataTableSkeleton } from "components/admin";
import { formatMoney, isRecoverableUploadResponseError } from "utils/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import RightDrawer from "components/global/RightDrawer";
import { PageHeader, StatusPill } from "components/admin/ui";
import { AdminUserForm } from "components/admin/forms";
import {
  UsersPrimaryButton,
  UsersInfoRow,
  UsersGridLayout,
  UsersThumbnailImage,
  UsersFieldInput,
  UsersLabelText,
  UsersMetaText,
  UsersFieldSelect,
} from "pages/app/users/styles/users.page.inline.styles";
const emptyUser = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  street_address: "",
  city: "",
  state: "",
  country: "",
  zip_code: "",
  role: "user",
  image: "",
  password: "",
  password_confirmation: "",
};
export const UsersPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const deferredSearch = useDeferredValue(searchValue);
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => adminApi.users.list(),
  });
  const saveMutation = useMutation({
    mutationFn: (payload: any) => {
      const normalizedPayload = {
        ...payload,
        password: payload.password || undefined,
        password_confirmation: payload.password_confirmation || undefined,
      };
      return selectedUser
        ? adminApi.users.update(selectedUser.id, normalizedPayload)
        : adminApi.users.create(normalizedPayload);
    },
    onSuccess: () => {
      toast.success(selectedUser ? "User updated." : "User created.");
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["showUserDetails"],
      });
      setSelectedUser(null);
      setDrawerOpen(false);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("User saved and profile image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        queryClient.invalidateQueries({
          queryKey: ["showUserDetails"],
        });
        setSelectedUser(null);
        setDrawerOpen(false);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to save this user.");
    },
  });
  const filteredUsers = users.filter((user: any) => {
    const matchesSearch = [
      user.full_name,
      user.email,
      user.role,
      user.country,
      user.city,
      user.player?.full_name,
    ]
      .join(" ")
      .toLowerCase()
      .includes(deferredSearch.trim().toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });
  const roles = Array.from(
    new Set(users.map((user: any) => user.role).filter(Boolean)),
  ) as string[];
  return (
    <AppLayout route="/users">
      <PageHeader
        title="Users"
        description="Handle account data, roles, profile details, and wallet visibility for admins, fans, and player-linked accounts."
        actions={
          <UsersPrimaryButton
            onClick={() => {
              setSelectedUser(null);
              setDrawerOpen(true);
            }}
            type="button"
          >
            <FaPlus size={12} />
            Add user
          </UsersPrimaryButton>
        }
      />

      <DataTable
        columns={[
          {
            label: "User",
            accessor: (row: any) => (
              <UsersInfoRow>
                <UsersThumbnailImage
                  alt={row.full_name || "User"}
                  fallbackLabel={row.full_name || row.email}
                  src={row.image}
                  variant="user"
                />
                <div>
                  <UsersLabelText>{row.full_name}</UsersLabelText>
                  <UsersMetaText>{row.email}</UsersMetaText>
                </div>
              </UsersInfoRow>
            ),
          },
          {
            label: "Role",
            accessor: (row: any) => <StatusPill value={row.role} />,
          },
          {
            label: "Location",
            accessor: (row: any) => [row.city, row.country].filter(Boolean).join(", ") || "Not set",
          },
          {
            label: "Wallet",
            accessor: (row: any) =>
              row.wallet ? formatMoney(row.wallet.balance) : "Not available",
          },
          {
            label: "Player Link",
            accessor: (row: any) => row.player?.full_name || "No linked athlete",
          },
        ]}
        data={filteredUsers}
        emptyDescription="Create a user record to start managing platform access from the admin portal."
        emptyTitle="No users match these filters"
        isLoading={isLoading}
        loadingState={<DataTableSkeleton columns={5} rows={5} showThumbnail />}
        onEdit={(user) => {
          setSelectedUser(user);
          setDrawerOpen(true);
        }}
        onView={(id) => history.push(`/users/${id}`)}
        toolbar={
          <UsersGridLayout>
            <UsersFieldInput
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search users by name, email, role, or location"
              value={searchValue}
            />
            <UsersFieldSelect
              onChange={(event) => setRoleFilter(event.target.value)}
              value={roleFilter}
            >
              <option value="all">All roles</option>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </UsersFieldSelect>
          </UsersGridLayout>
        }
      />

      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setSelectedUser(null);
          setDrawerOpen(false);
        }}
        title={selectedUser ? "Edit user" : "Create user"}
      >
        <AdminUserForm
          initialValues={
            selectedUser
              ? {
                  first_name: selectedUser.first_name || selectedUser.firstName || "",
                  last_name: selectedUser.last_name || selectedUser.lastName || "",
                  email: selectedUser.email || "",
                  phone_number: selectedUser.phone_number || selectedUser.phoneNumber || "",
                  street_address: selectedUser.street_address || selectedUser.streetAddress || "",
                  city: selectedUser.city || "",
                  state: selectedUser.state || "",
                  country: selectedUser.country || "",
                  zip_code: selectedUser.zip_code || selectedUser.zipCode || "",
                  role: selectedUser.role || "user",
                  image: selectedUser.image || "",
                  password: "",
                  password_confirmation: "",
                }
              : emptyUser
          }
          isSubmitting={saveMutation.isPending}
          onCancel={() => {
            setSelectedUser(null);
            setDrawerOpen(false);
          }}
          onSubmit={(values) => saveMutation.mutate(values)}
          requirePassword={!selectedUser}
          submitLabel={selectedUser ? "Save changes" : "Create user"}
        />
      </RightDrawer>
    </AppLayout>
  );
};
export default UsersPage;
